import AddIcon from "@mui/icons-material/Add";
import AddBoxIcon from "@mui/icons-material/AddBox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FolderIcon from "@mui/icons-material/Folder";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LinkIcon from "@mui/icons-material/Link";
import PreviewIcon from "@mui/icons-material/Preview";
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    Paper,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik } from "formik";
import { useState } from "react";
import CheckboxTree from "react-checkbox-tree";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import UserManagementService from "../../../../infrastructure/api/userManagement";
import { wrapFormikHandleChange } from "../../../../infrastructure/utils/formatters";
import { ReactComponent as DocumentDownloadIcon } from "../../../assets/icons/document-download.svg";
import { ReactComponent as PdfIcon } from "../../../assets/icons/pdf.svg";
import BreadcrumbsComponent from "../../../components/shared/breadcrumbs";
import SelectDefaultUrlDialog from "../../../components/user-management/SelectDefaultUrlDialog";

const validationSchema = Yup.object().shape({
  roleName: Yup.string().required("لطفا این فیلد را پر کنید."),
  roleDescription: Yup.string().required("لطفا این فیلد را پر کنید."), 
  defaultUrl: Yup.string().nullable()
});

export const treeIcons = {
  check: <CheckBoxIcon color="primary" />,
  uncheck: <CheckBoxOutlineBlankIcon color="gray" />,
  halfCheck: <IndeterminateCheckBoxIcon color="info" />,
  expandClose: <ChevronLeftIcon color="black" />,
  expandOpen: <KeyboardArrowDownIcon color="black" />,
  expandAll: <AddBoxIcon color="primary" />,
  collapseAll: <IndeterminateCheckBoxIcon color="info" />,
  parentClose: <FolderIcon color="gray" />,
  parentOpen: <FolderOpenIcon color="gray" />,
  leaf: <InsertDriveFileIcon color="gray" />,
};

const isPermissionLeafNode = (node) => {
  if (!node) return false;
  if (node.level === 3) return true;
  if (node.level === 2 && node.link) return true;
  if (node.link && (!node.child || node.child.length === 0)) return true;
  return false;
};

const collectPermissionLeafIds = (nodes, ids = []) => {
  if (!Array.isArray(nodes)) return ids;

  nodes.forEach((node) => {
    if (isPermissionLeafNode(node)) {
      ids.push(String(node.id));
    } else if (node.child?.length) {
      collectPermissionLeafIds(node.child, ids);
    }
  });

  return ids;
};

const buildPermissionsPayload = (permissionTree, checkedKeys, roleId) => {
  const checkedSet = new Set(checkedKeys);
  const leafIds = collectPermissionLeafIds(permissionTree);
  const numericRoleId =
    roleId !== "create" ? Number(roleId) || roleId : null;

  return leafIds.map((locationId) => {
    const numericLocationId = Number(locationId);
    return {
      roleId: numericRoleId,
      locationId: numericLocationId,
      show: checkedSet.has(`show-${locationId}`),
      create: checkedSet.has(`create-${locationId}`),
      edit: checkedSet.has(`edit-${locationId}`),
      delete: checkedSet.has(`delete-${locationId}`),
      pdf: checkedSet.has(`pdf-${locationId}`),
      excel: checkedSet.has(`excel-${locationId}`),
    };
  });
};

const onMakePermissionNodes = (nodes, roleId) => {
  if (Array.isArray(nodes)) {
    let checked = [],
      expanded = [];

    const array = nodes.map((node) => {
      const newNode = {
        value: String(node.id),
        label: node.title,
      };

      if (isPermissionLeafNode(node)) {
        newNode.children = [
          {
            value: `show-${node.id}`,
            label: "نمایش",
            locationId: node.id,
            roleId: roleId,
            icon: <PreviewIcon color="primary" />,
          },
          {
            value: `create-${node.id}`,
            label: "ایجاد",
            locationId: node.id,
            roleId: roleId,
            icon: <AddIcon color="success" />,
          },
          {
            value: `edit-${node.id}`,
            label: "ویرایش",
            locationId: node.id,
            roleId: roleId,
            icon: <EditIcon color="navy" />,
          },
          {
            value: `delete-${node.id}`,
            label: "حذف",
            locationId: node.id,
            roleId: roleId,
            icon: <DeleteIcon color="danger" />,
          },
          {
            value: `pdf-${node.id}`,
            label: "دانلود پی‌دی‌اف",
            locationId: node.id,
            roleId: roleId,
            icon: <PdfIcon color="danger" />,
          },
          {
            value: `excel-${node.id}`,
            label: "دانلود اکسل",
            locationId: node.id,
            roleId: roleId,
            icon: <DocumentDownloadIcon color="success" />,
          },
        ];

        if (node?.actionAccess) {
          if (node.actionAccess.show) {
            checked.push(`show-${node.id}`);
          }

          if (node.actionAccess.create) {
            checked.push(`create-${node.id}`);
          }

          if (node.actionAccess.edit) {
            checked.push(`edit-${node.id}`);
          }

          if (node.actionAccess.delete) {
            checked.push(`delete-${node.id}`);
          }

          if (node.actionAccess.pdf) {
            checked.push(`pdf-${node.id}`);
          }

          if (node.actionAccess.excel) {
            checked.push(`excel-${node.id}`);
          }

          if (checked.length > 0) {
            expanded.push(String(node.id));
          }
        }
      } else {
        const {
          nodes: children,
          checked: newChecked,
          expanded: newExpanded,
        } = onMakePermissionNodes(node.child, roleId);

        newNode.children = children;
        checked = [...checked, ...newChecked];
        expanded = [...expanded, ...newExpanded];

        // if (newExpanded.length > 0) {
        //   expanded.push(node.id);
        // }
      }

      return newNode;
    });

    return { nodes: array, checked, expanded };
  }
};

const onFindNodeItem = (nodes, key) => {
  if (key == null || key === "") return null;
  const wanted = String(key);
  for (let i = 0; i < nodes.length; i++) {
    if (String(nodes[i].value) === wanted) {
      return nodes[i];
    }
    if (nodes[i].children) {
      const result = onFindNodeItem(nodes[i].children, wanted);
      if (result) return result;
    }
  }
  return null;
};

const RoleInfoModifyPage = () => {
  const roleInfoDetail = useLoaderData("root");
  let navigate = useNavigate();
  const { id: roleId } = useParams();

  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(
    onMakePermissionNodes(roleInfoDetail.permission, roleId)
  );
  const [isShowSelectDefaultUrlDialog, setIsShowSelectDefaultUrlDialog] =
    useState(false);

  // ***************** [ Tree Functions ] *****************
  const onCheckTreeItem = (checked) => {
    setState((old) => ({ ...old, checked }));
  };

  const onExpandTreeParent = (expanded) => {
    setState((old) => ({ ...old, expanded }));
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);

    const numericRoleId =
      roleId !== "create" && roleId != null && String(roleId).trim() !== ""
        ? Number(roleId)
        : 0;

    const data = {
      role: {
        ...roleInfoDetail.role,
        ...formValue,
        roleId:
          Number(formValue.roleId) > 0
            ? Number(formValue.roleId)
            : Number(roleInfoDetail?.role?.roleId) > 0
            ? Number(roleInfoDetail.role.roleId)
            : numericRoleId,
        isActive: Boolean(formValue.isActive === true || formValue.isActive === "on"),
        isShowMainMenu: Boolean(
          formValue.isShowMainMenu === true || formValue.isShowMainMenu === "on"
        ),
      },
      permission: buildPermissionsPayload(
        roleInfoDetail.permission,
        state.checked,
        roleId
      ),
    };

    try {
      const res = await UserManagementService.modifyRoleInfoApi(
        data,
        data.role.roleId || null
      );

      if (res.isSuccess) {
        toast.success(res.message);
        navigate("/UM/Yeksa/RoleInfo");
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        "خطایی رخ داده است.";
      toast.error(apiMessage);

      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              roleId !== "create"
                ? `ویرایش ${roleInfoDetail.role.roleName}`
                : "ایجاد"
            }
          />

          <CardContent>
            <Formik
              initialValues={{
                roleId:
                  roleInfoDetail?.role?.roleId ??
                  (roleId !== "create" ? Number(roleId) || 0 : 0),
                roleName: roleInfoDetail?.role?.roleName ?? "",
                roleDescription: roleInfoDetail?.role?.roleDescription ?? "",
                defaultUrl: roleInfoDetail?.role?.defaultUrl ?? "",
                isActive: Boolean(roleInfoDetail?.role?.isActive),
                isShowMainMenu: Boolean(roleInfoDetail?.role?.isShowMainMenu),
              }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helpertext: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    {isShowSelectDefaultUrlDialog ? (
                      <SelectDefaultUrlDialog
                        routes={roleInfoDetail.permission}
                        open={isShowSelectDefaultUrlDialog}
                        currentDefaultUrl={values.defaultUrl || ""}
                        onClose={() => setIsShowSelectDefaultUrlDialog(false)}
                        onOk={(url) => {
                          setFieldValue("defaultUrl", url || "/", false);
                          setIsShowSelectDefaultUrlDialog(false);
                        }}
                      />
                    ) : null}

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="عنوان"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("roleName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="مسیر پیش فرض"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("defaultUrl")}
                          disabled={true}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LinkIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <Button
                                size="small"
                                color="primary"
                                variant="contained"
                                onClick={() =>
                                  setIsShowSelectDefaultUrlDialog(true)
                                }
                              >
                                انتخاب
                              </Button>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <TextField
                          label="شرح"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("roleDescription")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <FormControlLabel
                          label="فعال"
                          control={
                            <Switch
                              name="isActive"
                              checked={Boolean(values.isActive)}
                              onChange={(event) =>
                                setFieldValue("isActive", event.target.checked)
                              }
                            />
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={4}>
                        <FormControlLabel
                          label="نمایش منوی اصلی"
                          control={
                            <Switch
                              name="isShowMainMenu"
                              checked={Boolean(values.isShowMainMenu)}
                              onChange={(event) =>
                                setFieldValue(
                                  "isShowMainMenu",
                                  event.target.checked
                                )
                              }
                            />
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <Paper elevation={3} sx={{ p: 2 }}>
                          <Box sx={{ mb: 1 }}>
                            <Typography variant="title" fontWeight={"bold"}>
                              دسترسی‌ها
                            </Typography>
                          </Box>
                          <Box>
                            <CheckboxTree
                              nodes={state.nodes}
                              checked={state.checked}
                              expanded={state.expanded}
                              onCheck={onCheckTreeItem}
                              onExpand={onExpandTreeParent}
                              icons={treeIcons}
                              showExpandAll
                            />
                          </Box>
                        </Paper>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={() => {
                            navigate("/UM/Yeksa/RoleInfo");
                          }}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {roleId !== "create" ? "ویرایش" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default RoleInfoModifyPage;
